import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FolderIcon from "@mui/icons-material/Folder";
import { Avatar, Badge, Box, IconButton, List, ListItemAvatar, ListItemButton, ListItemText, Paper, Tooltip, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { createScopedLogger } from "../../../common";
import IconComponent from "../../../common/components/IconComponent";
import LoadingOverlay from "../../../common/components/LoadingOverlay";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useDesignerMode } from "../../Contexts/DesignerModeContext";
import { useChildContext } from "../../Hooks/useChildContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useMaskPiiSetting from "../../Hooks/useMaskPiiSetting";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ListWithSideDetailDirection, ListWithSideDetailItemModel, ListWithSideDetailModel } from "../../Models/ListWithSideDetailModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import { coerceListItems, formatListFieldValue, getListFieldValue } from "../ListShared/listItemDisplay";
import ListPanelHeader from "../ListShared/ListPanelHeader";
import { useSelectableListItems } from "../ListShared/useSelectableListItems";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("ListWithSideDetailComponent");
const LIST_WITH_SIDE_DETAIL_ITEMS_PATH_SEGMENT = "items";
const LIST_WITH_SIDE_DETAIL_ITEM_TYPE = "ListWithSideDetailItemModel";

function getItemTemplate(component: ListWithSideDetailModel): ListWithSideDetailItemModel {
  return component.Items?.[0] ?? component;
}

type ListWithSideDetailItemButtonProps = {
  component: ListWithSideDetailModel;
  componentPath: string[];
  itemKey: string | number;
  index: number;
  selected: boolean;
  primary: React.ReactNode;
  secondary: React.ReactNode;
  supporting: React.ReactNode;
  badge: React.ReactNode;
  icon: React.ReactNode;
  onSelect: () => void;
};

const ListWithSideDetailItemButton: React.FC<ListWithSideDetailItemButtonProps> = ({
  component,
  componentPath,
  itemKey,
  index,
  selected,
  primary,
  secondary,
  supporting,
  badge,
  icon,
  onSelect,
}) => {
  const selectionProps = useComponentSelection({
    component: {
      Id: `${component.Id}-item-${itemKey}`,
      Type: LIST_WITH_SIDE_DETAIL_ITEM_TYPE,
      Layout: {},
    },
    componentPath: [...componentPath, LIST_WITH_SIDE_DETAIL_ITEMS_PATH_SEGMENT, String(index)],
    isContainer: false,
  });

  return (
    <Box {...selectionProps} sx={{ display: "block" }}>
      <ListItemButton
        selected={selected}
        onMouseDown={(event) => {
          if (IsDesignMode && (event.ctrlKey || event.metaKey)) {
            onSelect();
          }
        }}
        onClick={onSelect}
        sx={{ alignItems: "center", gap: 1 }}
      >
        <ListItemAvatar sx={{ minWidth: 40, display: "flex", alignItems: "center" }}>
          <Badge badgeContent={badge} color="primary">
            <Avatar sx={{ width: 32, height: 32 }}>{icon}</Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={String(primary ?? "")}
          secondary={
            <>
              {secondary !== undefined && <span>{String(secondary)}</span>}
              {supporting !== undefined && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {String(supporting)}
                </Typography>
              )}
            </>
          }
        />
      </ListItemButton>
    </Box>
  );
};

const ListWithSideDetailComponent: React.FC<IComponentProps<ListWithSideDetailModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const designerMode = useDesignerMode();
  const bundleContext = React.useContext(BundleContext);
  const [maskPii] = useMaskPiiSetting();
  const currencyCode = bundleContext.Settings?.CurrencyCode;
  const phoneNumberFormat = bundleContext.Settings?.PhoneNumberFormat;
  const componentLayoutSx = getComponentLayout(component);
  const keyField = component.KeyField ?? "Id";
  const persistedKey = component.PersistedStateKey ?? `listWithSideDetail.${component.Id}`;

  const [items, itemsPending, refreshItems] = useObservedExpression<any[]>(component.GetItems, context, "ListWithSideDetailComponent.GetItems", (value) => {
    try {
      return coerceListItems(value, "ListWithSideDetail GetItems");
    } catch (error) {
      logger.error("GetItems expression did not return an array or an object with an Items array:", value);
      throw error;
    }
  });
  const [currentItem] = useObservedExpression<any>(component.Item, context, "ListWithSideDetailComponent.Item");
  const handleItemSelected = React.useCallback(
    async (item: any) => {
      if (component.OnItemSelected) {
        const eventContext = context.CreateChild();
        eventContext.VariableScope.set(VariableNames.Item, item);
        eventContext.VariableScope.set(VariableNames.Event, item);
        await eventContext.Evaluator.evaluate(component.OnItemSelected, eventContext);
      }
    },
    [component.OnItemSelected, context]
  );
  const { selectedKey, selectedItem, selectItem, clearSelection, getItemKey } = useSelectableListItems({
    componentId: component.Id,
    componentPath,
    itemPathSegment: LIST_WITH_SIDE_DETAIL_ITEMS_PATH_SEGMENT,
    items,
    itemsPending,
    keyField,
    persistedStateKey: persistedKey,
    persistSelection: component.PersistSelection,
    itemQueryKey: component.ItemQueryKey,
    selectedPath: designerMode?.selectedPath,
    itemExpression: component.Item,
    currentItem,
    context,
    onSelected: handleItemSelected,
    onError: errorHandler,
  });
  const detailContextVariables = useMemo(() => ({ [VariableNames.Item]: selectedItem }), [selectedItem]);
  const detailContext = useChildContext(context, detailContextVariables);
  const detailChild = component.Children?.[0];
  const itemTemplate = getItemTemplate(component);
  const listWidth = component.ListWidth ?? 320;
  const direction = component.Direction ?? ListWithSideDetailDirection.Right;
  const detailOnLeft = direction === ListWithSideDetailDirection.Left;
  const showDetailPane = !!detailChild && selectedItem !== undefined && selectedItem !== null;
  const minDetailWidth = showDetailPane ? 320 : 0;

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: !items || items.length === 0,
  });

  const headerTitle = component.HeaderTitle ?? component.Title;
  const header = (
    <ListPanelHeader
      title={headerTitle}
      iconName={component.HeaderIcon}
      showRefresh={true}
      isRefreshing={itemsPending}
      onRefresh={() => refreshItems()}
      actions={component.HeaderActions}
      actionData={selectedItem}
      context={context}
      ownerComponentId={component.Id}
      ownerComponentPath={componentPath}
      leadingActions={
        showDetailPane ? (
          <Tooltip title="Close detail">
            <IconButton size="small" onClick={clearSelection} aria-label="close detail">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    />
  );

  const listPane = (
    <Box
      sx={{
        width: listWidth,
        flex: `0 0 ${listWidth}px`,
        borderLeft: detailOnLeft ? 1 : undefined,
        borderRight: detailOnLeft ? undefined : 1,
        borderColor: "divider",
        overflow: "auto",
      }}
    >
      {header}
      <List dense disablePadding>
        {(items ?? []).map((item, index) => {
          const key = getItemKey(item, index);
          const primaryFieldValue = getListFieldValue(item, itemTemplate.PrimaryTextField);
          const primary =
            formatListFieldValue(
              primaryFieldValue,
              itemTemplate.PrimaryTextField,
              "Primary Text",
              itemTemplate.PrimaryTextFormat,
              currencyCode,
              phoneNumberFormat,
              maskPii
            ) ??
            item?.Label ??
            item?.Name ??
            key;
          const secondary = formatListFieldValue(
            getListFieldValue(item, itemTemplate.SecondaryTextField),
            itemTemplate.SecondaryTextField,
            "Secondary Text",
            itemTemplate.SecondaryTextFormat,
            currencyCode,
            phoneNumberFormat,
            maskPii
          );
          const supporting = formatListFieldValue(
            getListFieldValue(item, itemTemplate.SupportingTextField),
            itemTemplate.SupportingTextField,
            "Supporting Text",
            itemTemplate.SupportingTextFormat,
            currencyCode,
            phoneNumberFormat,
            maskPii
          );
          const badge = formatListFieldValue(
            getListFieldValue(item, itemTemplate.BadgeField),
            itemTemplate.BadgeField,
            "Badge",
            itemTemplate.BadgeFormat,
            currencyCode,
            phoneNumberFormat,
            maskPii
          );
          const icon = itemTemplate.IconName ? <IconComponent iconName={itemTemplate.IconName} /> : <FolderIcon />;

          return (
            <ListWithSideDetailItemButton
              key={key}
              component={component}
              componentPath={componentPath}
              itemKey={key}
              index={index}
              selected={selectedKey !== null && String(selectedKey) === String(key)}
              primary={primary}
              secondary={secondary}
              supporting={supporting}
              badge={badge}
              icon={icon}
              onSelect={() => selectItem(item, index)}
            />
          );
        })}
      </List>
    </Box>
  );
  const detailPane = showDetailPane ? (
    <Box sx={{ flex: "1 0 320px", minWidth: minDetailWidth, overflow: "auto" }}>
      <ComponentFactory parent={component} component={detailChild} context={detailContext} componentPath={[...componentPath, detailChild.Id]} />
    </Box>
  ) : null;
  const surfaceSx = {
    ...componentLayoutSx,
    position: "relative",
    width: componentLayoutSx.width ?? (showDetailPane ? "max-content" : listWidth),
    minHeight: 360,
    overflow: "hidden",
    ...(showDetailPane ? { minWidth: listWidth + minDetailWidth } : {}),
  } as const;
  const contentSx = {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "stretch",
    width: showDetailPane ? "max-content" : listWidth,
    minHeight: componentLayoutSx.minHeight ?? 360,
    ...(showDetailPane ? { minWidth: listWidth + minDetailWidth } : {}),
  } as const;
  const listWithSideDetailContent = (
    <>
      <LoadingOverlay loading={itemsPending} />
      <Box sx={contentSx}>
        {detailOnLeft && detailPane}
        {listPane}
        {!detailOnLeft && detailPane}
      </Box>
    </>
  );
  const listWithSideDetailSurface =
    component.UsePaper === false ? (
      <Box sx={surfaceSx} {...selectionProps}>
        {listWithSideDetailContent}
      </Box>
    ) : (
      <Paper elevation={1} sx={surfaceSx} {...selectionProps}>
        {listWithSideDetailContent}
      </Paper>
    );

  return listWithSideDetailSurface;
};

export default ListWithSideDetailComponent;
